import { inject, Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { concatMap, map, catchError, of, withLatestFrom } from "rxjs";
import { PaymentTermsClient } from "src/api-service";
import { selectSelectedCompany } from "../../../../user/user.selectors";
import { PaymentTermsActions } from "./payment-terms.actions";
import { DataActions } from "../../../../application/data/data.actions";

@Injectable()
export class PaymentTermsEffects {
    private readonly actions$ = inject(Actions);
    private readonly store = inject(Store);
    private readonly paymentTermsClient = inject(PaymentTermsClient);

    load$ = createEffect(() =>
        this.actions$.pipe(
            ofType(PaymentTermsActions.load),
            withLatestFrom(this.store.select(selectSelectedCompany)),
            concatMap(([, selectedCompany]) =>
                this.paymentTermsClient
                    .getAllAdmin(selectedCompany.id, 1, 1000)
                    .pipe(
                        map(response =>
                            PaymentTermsActions.loadSuccess({ paymentTerms: response.data ?? [] }),
                        ),
                        catchError(error =>
                            of(
                                PaymentTermsActions.loadFailure({
                                    error,
                                    toastKey: "ADMIN_PANEL.EDIT_PAYMENT_TERM_SECTIONS.TOAST.LOAD_ERROR",
                                }),
                            ),
                        ),
                    ),
            ),
        ),
    );

    createPaymentTerm$ = createEffect(() =>
        this.actions$.pipe(
            ofType(PaymentTermsActions.createPaymentTerm),
            withLatestFrom(this.store.select(selectSelectedCompany)),
            concatMap(([{ request }, selectedCompany]) =>
                this.paymentTermsClient.create(request, selectedCompany.id).pipe(
                    concatMap(() => [
                        PaymentTermsActions.createPaymentTermSuccess({
                            toastKey: "ADMIN_PANEL.EDIT_PAYMENT_TERM_SECTIONS.TOAST.CREATE_SUCCESS",
                        }),
                        PaymentTermsActions.load(),
                        DataActions.initializeData({ companyId: selectedCompany.id }),
                    ]),
                    catchError(error =>
                        of(
                            PaymentTermsActions.createPaymentTermFailure({
                                error,
                                toastKey: "ADMIN_PANEL.EDIT_PAYMENT_TERM_SECTIONS.TOAST.CREATE_ERROR",
                            }),
                        ),
                    ),
                ),
            ),
        ),
    );

    updatePaymentTerm$ = createEffect(() =>
        this.actions$.pipe(
            ofType(PaymentTermsActions.updatePaymentTerm),
            withLatestFrom(this.store.select(selectSelectedCompany)),
            concatMap(([{ request, id }, selectedCompany]) =>
                this.paymentTermsClient.update(id, request, selectedCompany.id).pipe(
                    concatMap(() => [
                        PaymentTermsActions.updatePaymentTermSuccess({
                            toastKey: "ADMIN_PANEL.EDIT_PAYMENT_TERM_SECTIONS.TOAST.UPDATE_SUCCESS",
                        }),
                        PaymentTermsActions.load(),
                        DataActions.initializeData({ companyId: selectedCompany.id }),
                    ]),
                    catchError(error =>
                        of(
                            PaymentTermsActions.updatePaymentTermFailure({
                                error,
                                toastKey: "ADMIN_PANEL.EDIT_PAYMENT_TERM_SECTIONS.TOAST.UPDATE_ERROR",
                            }),
                        ),
                    ),
                ),
            ),
        ),
    );

    deletePaymentTerm$ = createEffect(() =>
        this.actions$.pipe(
            ofType(PaymentTermsActions.deletePaymentTerm),
            withLatestFrom(this.store.select(selectSelectedCompany)),
            concatMap(([{ id }, selectedCompany]) =>
                this.paymentTermsClient.delete(id, selectedCompany.id).pipe(
                    concatMap(() => [
                        PaymentTermsActions.deletePaymentTermSuccess({
                            toastKey: "ADMIN_PANEL.EDIT_PAYMENT_TERM_SECTIONS.TOAST.DELETE_SUCCESS",
                        }),
                        PaymentTermsActions.load(),
                        DataActions.initializeData({ companyId: selectedCompany.id }),
                    ]),
                    catchError(error =>
                        of(
                            PaymentTermsActions.deletePaymentTermFailure({
                                error,
                                toastKey: "ADMIN_PANEL.EDIT_PAYMENT_TERM_SECTIONS.TOAST.DELETE_ERROR",
                            }),
                        ),
                    ),
                ),
            ),
        ),
    );
}
